import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Film, Tv, Zap } from "lucide-react";
import { Hero } from "@/components/catalog/Hero";
import { Rail } from "@/components/catalog/Rail";
import { Spinner } from "@/components/catalog/TitleGrid";
import { HomeFeatures, LiveRail } from "@/components/home/HomeSections";
import { useProfiles } from "@/context/ProfileContext";
import { api } from "@/lib/api";
import { useCreatorOn } from "@/lib/platform";

const SEE_ALL = {
  originals: "/originals", movies: "/movies", series: "/series", shorts: "/shorts",
  action: "/originals?genre=Action", romance: "/originals?genre=Romance", scifi: "/originals?genre=Sci-Fi",
  thrillers: "/originals?genre=Thriller", recent: "/originals", continue_watching: "/library?tab=continue",
};

const CATEGORIES = [
  { label: "Originals", to: "/originals", icon: Crown },
  { label: "Movies", to: "/movies", icon: Film },
  { label: "Series", to: "/series", icon: Tv },
  { label: "Shorts", to: "/shorts", icon: Zap },
];

export default function Home() {
  const creatorOn = useCreatorOn();
  const { activeProfile } = useProfiles();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/catalog/home").then((r) => setData(r.data)).catch(() => setData({ hero: null, sections: [] }));
  }, [activeProfile?.id]);

  if (!data) return <div className="pt-24"><Spinner testId="home-loading" /></div>;

  return (
    <div data-testid="home-page">
      {data.hero && <Hero title={data.hero} />}
      <div className={`relative z-10 space-y-2 sm:space-y-3 ${data.hero ? "-mt-20 sm:-mt-24 md:-mt-32" : "pt-28"}`}>
        {data.sections.map((s, i) => (
          <div key={s.key}>
            <Rail title={s.title} items={s.items} wide={s.style === "wide"} seeAll={SEE_ALL[s.key]} testId={`rail-${s.key}`} />
            {i === 0 && (
              <div className="mx-auto max-w-[1680px] px-4 sm:px-6 lg:px-10 py-3 flex gap-2 sm:gap-3 overflow-x-auto no-scrollbar" data-testid="home-category-pills">
                {CATEGORIES.map(({ label, to, icon: Icon }) => (
                  <Link key={to} to={to} data-testid={`category-pill-${label.toLowerCase()}`} className="btn-ghost-gold flex-none px-5 py-2 text-sm">
                    <Icon className="h-4 w-4" /> {label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mx-auto mt-12 max-w-[1680px] space-y-12">
        <LiveRail />
        <HomeFeatures />
      </div>
      {creatorOn && <section className="mx-auto mt-16 max-w-[1680px] px-4 sm:px-6 lg:px-10" data-testid="home-tagline-banner">
        <div className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/25 bg-[#0B0B0D] px-6 sm:px-12 py-12 sm:py-16">
          <img src="/assets/art/bd_helios.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0D] via-[#0B0B0D]/85 to-transparent" />
          <div className="relative max-w-2xl space-y-4">
            <p className="eyebrow">Coming to AETRYX</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight gold-text">Watch movies. Create movies. Build your own cinematic universe.</h2>
            <p className="text-sm sm:text-base text-white/70">AETRYX Create Studio puts an AI film crew in your hands — script, storyboard, score and publish.</p>
            <Link to="/create" data-testid="home-banner-create-btn" className="btn-gold inline-flex px-7 py-3 text-sm">Explore Create Studio</Link>
          </div>
        </div>
      </section>}
    </div>
  );
}
